import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import InputsNew from "../../components/common/InputsNew";
import dftarrowback from "../../assets/task/back_arrow.svg"
import BreadCumb from "../../components/common/Breadcrumb";
import Button from "../../components/common/Button";
import {
    EMPLOYEES_BY_AGENCY_ID,
    GET_EMPLOYEE_PEER_POINTS_FOR_MONTH,
    GET_EMPLOYEE_PEER_POINTS_FOR_MONTH_DETAILED
} from "../../api/query";
import { CREATE_EMPLOYEE_PEER } from "../../api/mutation";
import { useAuth } from "../../context/AuthContext";
import Loader from "../../components/common/Loader";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import { format } from "date-fns";


const MONTHS = [
    { key: "1", name: "January" },
    { key: "2", name: "February" },
    { key: "3", name: "March" },
    { key: "4", name: "April" },
    { key: "5", name: "May" },
    { key: "6", name: "June" },
    { key: "7", name: "July" },
    { key: "8", name: "August" },
    { key: "9", name: "September" },
    { key: "10", name: "October" },
    { key: "11", name: "November" },
    { key: "12", name: "December" },
];

const YEARS = [
    { key: "2023", name: "2023" },
    { key: "2024", name: "2024" },
    { key: "2025", name: "2025" },
];

const PeerPoints = () => {
    const { userInfo } = useAuth();
    const organizationID = userInfo?.agencies?.id || '';
    const employeeId = userInfo?.employee_id || '';

    // Get current month and year
    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1).toString();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonthName = MONTHS.find(m => m.key === currentMonth)?.name || "";

    const [selectedMonthKey, setSelectedMonthKey] = useState(currentMonth);
    const [selectedMonthName, setSelectedMonthName] = useState(currentMonthName);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [showGivePointsModal, setShowGivePointsModal] = useState(false);
    const [activeTab, setActiveTab] = useState<"given" | "received">("given");

    // Modal form state
    const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
    const [points, setPoints] = useState("");
    const [comment, setComment] = useState("");

    // Check if current month and year are selected
    const isCurrentMonth = selectedMonthKey === currentMonth && selectedYear === currentYear;

    // Fetch employees
    const { loading: employeesLoading, data: employeesData } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
        variables: {
            agency_id: parseInt(organizationID)
        },
        skip: !organizationID,
        fetchPolicy: 'no-cache'
    });

    // Fetch peer points summary
    const { loading: summaryLoading, data: summaryData, refetch: refetchSummary } = useQuery(GET_EMPLOYEE_PEER_POINTS_FOR_MONTH, {
        variables: {
            employee_id: parseInt(employeeId),
            month: parseInt(selectedMonthKey),
            year: parseInt(selectedYear)
        },
        skip: !employeeId,
        fetchPolicy: 'no-cache'
    });

    // Fetch detailed peer points transactions
    const { loading: detailsLoading, data: detailsData, refetch: refetchDetails } = useQuery(GET_EMPLOYEE_PEER_POINTS_FOR_MONTH_DETAILED, {
        variables: {
            employee_id: parseInt(employeeId),
            month: parseInt(selectedMonthKey),
            year: parseInt(selectedYear)
        },
        skip: !employeeId,
        fetchPolicy: 'no-cache'
    });

    // Create peer points mutation
    const [createPeerPoints, { loading: createLoading }] = useMutation(CREATE_EMPLOYEE_PEER);

    // Transform employees list for dropdown - exclude current employee
    const employeeOptions = useMemo(() => {
        if (!employeesData?.employees_by_agency_id?.employees || !employeeId) return [];

        const currentEmployeeId = parseInt(employeeId);

        return employeesData.employees_by_agency_id.employees
            .filter((emp: { employee_id: number }) => {
                // Exclude the current employee from the dropdown
                return emp.employee_id != currentEmployeeId;
            })
            .map((emp: { employee_id: number; first_name: string; last_name: string }) => ({
                key: emp.employee_id.toString(),
                name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Employee'
            }));
    }, [employeesData, employeeId]);

    // Get summary data
    const summary = summaryData?.get_employee_peer_points_for_month?.summary;
    const balance = summary?.remaining_balance || 0;
    const totalGiven = summary?.points_given || 0;
    const totalReceived = summary?.points_earned || 0;
    
    // Transform detailed transactions
    const pointsGiven = useMemo(() => {
        if (!detailsData?.get_employee_peer_points_for_month_detailed?.points_given) return [];
        return detailsData.get_employee_peer_points_for_month_detailed.points_given.map((item: any) => {
            return {
                id: item.id,
                type: "given",
                employee: item.receiver_full_name || 'Unknown',
                points: item.point,
                comment: item.reason || '',
                date: format(item.created_on, 'MM-dd-yyyy')
            };
        });
    }, [detailsData]);

    const pointsReceived = useMemo(() => {
        if (!detailsData?.get_employee_peer_points_for_month_detailed?.points_earned) return [];
        return detailsData.get_employee_peer_points_for_month_detailed.points_earned.map((item: any) => {
            return {
                id: item.id,
                type: "received",
                employee: item.giver_full_name || 'Unknown',
                points: item.point,
                comment: item.reason || '',
                date: item.created_on ? new Date(item.created_on).toISOString().split('T')[0] : ''
            };
        });
    }, [detailsData]);

    // Filter transactions based on active tab
    const filteredTransactions = activeTab === "given" ? pointsGiven : pointsReceived;

    const handleGivePoints = async () => {
        if (!selectedEmployeeId || !points || parseInt(points) > 25 || parseInt(points) < 1) {
            showError("Please select an employee and enter valid points (1-25)");
            return;
        }

        if (!comment) {
            showError("Please enter comment");
            return;
        }

        if (balance - parseInt(points) < 0) {
            showError("Balance issue");
            return;
        }

        try {
            const result = await createPeerPoints({
                variables: {
                    input: {
                        employee_id: parseInt(selectedEmployeeId),
                        point: parseInt(points),
                        reason: comment || "",
                        given_by: parseInt(employeeId),
                        agency_id: parseInt(organizationID),
                        created_by: parseInt(employeeId)
                    }
                }
            });

            if (result.data?.create_employee_peer_points?.status === "Success") {
                showSuccess(result.data.create_employee_peer_points.message || "Points given successfully!");

                // Reset form
                setSelectedEmployeeId("");
                setPoints("");
                setComment("");
                setShowGivePointsModal(false);

                // Refetch data
                refetchSummary();
                refetchDetails();
            } else {
                showError(result.data?.create_employee_peer_points?.message || "Failed to give points");
            }
        } catch (error: any) {
            console.error("Error giving points:", error);
            showError(error.message || "An error occurred while giving points");
        }
    };

    // Refetch data when month or year changes
    useEffect(() => {
        if (employeeId) {
            refetchSummary();
            refetchDetails();
        }
    }, [selectedMonthKey, selectedYear, employeeId]);

    if (employeesLoading || summaryLoading || detailsLoading || createLoading) {
        return <Loader />;
    }

    return (
        <>
            <div id="cts-respon-p-dv" className="w-full flex flex-col items-start justify-start py-40 px-20  mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full">

                {/* Header */}
                <div className="grid gap-4">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] max-w-full">
                        <img
                            className="h-6 w-6 relative overflow-hidden shrink-0  dark-white-img cursor-pointer"
                            loading="lazy"
                            alt=""
                            src={dftarrowback}
                            // onClick={onBackClick}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base">
                            Peer Points
                        </h3>
                    </div>
                </div>


                {/* Month and Year Dropdowns */}
                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                    <div className="flex-1">
                        <SearchableInputsDropdown
                            label="Month"
                            placeHolder="Select Month"
                            options={MONTHS}
                            initialValue={selectedMonthName}
                            onSelect={(key: string, name: string) => {
                                setSelectedMonthKey(key);
                                setSelectedMonthName(name);
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <SearchableInputsDropdown
                            label="Year"
                            placeHolder="Select Year"
                            options={YEARS}
                            initialValue={selectedYear}
                            onSelect={(_key: string, name: string) => setSelectedYear(name)}
                        />
                    </div>
                </div>

                {/* Cards Section */}
                <div className="w-full grid grid-cols-3 gap-4 mb-6 mq991:grid-cols-1">
                    {/* Balance Card */}
                    <div className="bg-white dark:bg-slate-700 border-peer-p rounded-lg shadow-md border border-gray-200 dark:border-slate-600 self-stretch flex-1 services-item-box flex flex-col items-start   gap-[8px] text-dimgray-200 p-6 peer-bg-item">
                        <div className="flex gap-6 items-center">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
                                <svg xmlns="http://www.w3.org/2000/svg" height="30px" width="30px" version="1.1" id="_x32_" viewBox="0 0 512 512" fill="#fff">
                                    <style type="text/css">

                                    </style>
                                    <g>
                                        <path className="st0" d="M512,292.069h-0.442l-81.151-186.586c9.816,0.74,17.961,1.439,24.078,2.012l2.362-25.581   c-27.16-2.514-92.696-7.342-163.61-8.702c-1.774-6.657-5.298-12.639-9.998-17.348c-6.932-6.953-16.621-11.272-27.232-11.272   c-10.615,0-20.327,4.319-27.26,11.272c-4.701,4.709-8.224,10.691-9.971,17.348c-70.91,1.36-136.453,6.189-163.633,8.702   l2.39,25.597c6.085-0.572,14.254-1.257,24.046-2.004h0.003L0.45,292.069H0c0.06,0.207,0.131,0.398,0.191,0.589l-0.167,0.389   l0.334,0.143c12.567,39.836,49.786,68.734,93.782,68.734c43.992,0,81.203-28.89,93.774-68.734l0.335-0.143l-0.164-0.389   c0.06-0.192,0.132-0.382,0.188-0.589h-0.442l-81.882-188.28c31.566-2.052,72.396-4.137,114.884-4.948   c1.93,4.327,4.622,8.233,7.915,11.518c3.361,3.388,7.37,6.141,11.837,8.097v271.086c-17.393,0-30.943,0-30.943,0   s-7.413,46.342-40.794,53.756v24.11h174.308v-24.11c-33.38-7.414-40.798-53.756-40.798-53.756s-13.55,0-30.942,0V118.456h0.004   c4.45-1.956,8.46-4.709,11.82-8.097c3.317-3.285,6.014-7.191,7.938-11.518c42.488,0.811,83.31,2.896,114.881,4.948l-81.883,188.28   h-0.449c0.06,0.207,0.131,0.398,0.191,0.589l-0.167,0.389l0.334,0.143c12.568,39.836,49.786,68.734,93.782,68.734   c43.992,0,81.202-28.89,93.774-68.734l0.334-0.143l-0.163-0.389C511.873,292.467,511.945,292.276,512,292.069z M171.015,292.069   H17.253L94.14,115.291L171.015,292.069z M270.525,97.647c-0.334,0.335-0.668,0.644-1.026,0.923   c-3.604,3.214-8.28,5.107-13.491,5.107c-5.218,0-9.891-1.893-13.519-5.107c-0.357-0.278-0.691-0.588-1.025-0.923   c-3.723-3.722-6.01-8.813-6.01-14.524c0-3.722,0.974-7.191,2.692-10.197c0.927-1.591,2.032-3.039,3.318-4.343   c3.75-3.722,8.837-6.006,14.544-6.006c5.704,0,10.79,2.283,14.517,6.006c1.309,1.304,2.414,2.752,3.318,4.343   c1.741,3.006,2.72,6.475,2.72,10.197C276.562,88.834,274.276,93.925,270.525,97.647z M340.985,292.069l76.883-176.778   l76.88,176.778H340.985z" />
                                    </g>
                                </svg>
                            </div>
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-300 my-0">Balance</h3>
                        </div>
                        <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{balance}</p>
                    </div>

                    {/* Given Card */}
                    <div className="bg-white dark:bg-slate-700 border-peer-g rounded-lg shadow-md border border-gray-200 dark:border-slate-600 self-stretch flex-1 services-item-box flex flex-col items-start  gap-[8px] text-dimgray-200 p-6 peer-bg-item">
                        <div className="flex gap-4 items-center ">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center shadow-md">
                                <svg xmlns="http://www.w3.org/2000/svg" height="30px" width="30px" viewBox="0 0 800 706" fill="#fff">
                                    <path d="M588.46 6.75391C471.625 6.75391 376.918 101.467 376.918 218.296C376.918 335.127 471.625 429.843 588.46 429.843C705.291 429.843 800.001 335.127 800.001 218.296C800.001 101.467 705.291 6.75391 588.46 6.75391ZM588.46 380.817C498.701 380.817 425.942 308.052 425.942 218.299C425.942 128.546 498.701 55.7826 588.46 55.7826C678.216 55.7826 750.972 128.546 750.972 218.299C750.972 308.052 678.216 380.817 588.46 380.817Z" fill="#fff" />
                                    <path d="M685.1 187.2C685.1 199.4 682.3 210.6 676.7 220.8C671.1 230.8 662.5 238.9 650.9 245.1C639.3 251.3 624.9 254.4 607.7 254.4H575.9V330H524.6V119.4H607.7C624.5 119.4 638.7 122.3 650.3 128.1C661.9 133.9 670.6 141.9 676.4 152.1C682.2 162.3 685.1 174 685.1 187.2ZM603.8 213.6C613.6 213.6 620.9 211.3 625.7 206.7C630.5 202.1 632.9 195.6 632.9 187.2C632.9 178.8 630.5 172.3 625.7 167.7C620.9 163.1 613.6 160.8 603.8 160.8H575.9V213.6H603.8Z" fill="#fff" />
                                    <path d="M757.016 532.353C712.032 557.181 650.162 580.137 588.462 589.815C533.566 598.424 430.291 577.661 430.291 553.14C430.291 543.848 520.867 576.035 575.656 561.418C617.67 550.209 611.706 510.907 588.854 505.724C566.134 500.567 433.028 451.804 376.173 450.122C349.39 449.331 283.268 459.437 251.272 464.648C240.311 466.431 233.438 466.235 232.753 476.724C229.569 525.473 223.294 625.163 219.873 677.236C219.801 678.337 221.33 693.133 233.54 682.72C246.904 671.324 265.648 658.693 282.986 662.625C310.186 668.796 545.501 711.906 577.959 704.362C624.392 693.568 755.837 615.671 781.039 591.458C820.101 553.922 789.736 514.294 757.016 532.353Z" fill="#fff" />
                                    <path d="M167.818 462.749L44.5424 456.674C35.0248 456.205 25.3043 463.378 22.939 472.607L0.52383 676.115C-1.83922 685.346 4.0188 693.202 13.5409 693.57L150.16 693.556C159.685 693.924 168.068 686.451 168.799 676.949L183.802 480.875C184.528 471.376 177.335 463.218 167.818 462.749ZM95.2442 666.595C76.4187 666.595 61.1604 651.335 61.1604 632.511C61.1604 613.686 76.421 598.428 95.2442 598.428C114.067 598.428 129.328 613.688 129.328 632.511C129.328 651.337 114.07 666.595 95.2442 666.595Z" fill="#fff" />
                                </svg>
                            </div>
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-300 my-0">Given</h3>
                        </div>
                        <p className="text-3xl font-bold text-red-600 dark:text-red-400">{totalGiven}</p>
                    </div>

                    {/* Received Card */}
                    <div className="bg-white dark:bg-slate-700 border-peer-y rounded-lg shadow-md border border-gray-200 dark:border-slate-600 self-stretch flex-1 services-item-box flex flex-col items-start  gap-[8px] text-dimgray-200 p-6 peer-bg-item">
                        <div className="flex gap-4 items-center">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-700 flex items-center justify-center shadow-md">
                                <svg xmlns="http://www.w3.org/2000/svg" height="30px" width="30px" viewBox="0 0 800 800" fill="none">
                                    <path d="M166.668 583.335L633.335 116.668" stroke="#fff" stroke-width="70" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M166.668 241V583.334H509.001" stroke="#fff" stroke-width="70" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M116.668 733.332H683.335" stroke="#fff" stroke-width="70" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-300 my-0">Received</h3>
                        </div>
                        <p className="text-3xl font-bold text-green-600 dark:text-green-400">{totalReceived}</p>
                    </div>
                </div>

                {/* Give Points Button - Only show for current month */}
                {isCurrentMonth && (
                    <div className="w-full mb-6 justify-end flex">
                        <div>
                            <Button
                                title="Give Points"
                                variant="primary"
                                onClick={() => setShowGivePointsModal(true)}
                                className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface  flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                            />
                        </div>
                    </div>
                )}

                {/* Transactions List with Tabs */}
                <div className="flex border-b border-gray-200 dark:border-slate-600 peer-tab-main w-full">
                    <button
                        className={`flex-1 py-4 px-6 font-semibold text-sm transition-colors ${activeTab === "given"
                            ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                            }`}
                        onClick={() => setActiveTab("given")}
                    >
                        Given ({pointsGiven.length})
                    </button>
                    <button
                        className={`flex-1 py-4 px-6 font-semibold text-sm transition-colors ${activeTab === "received"
                            ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                            }`}
                        onClick={() => setActiveTab("received")}
                    >
                        Received ({pointsReceived.length})
                    </button>
                </div>

                <div className="w-full bg-white dark:bg-slate-700 rounded-lg shadow-md border border-gray-200 dark:border-slate-600 gap-6 grid">
                    {/* Tabs Header */}

                    {/* Tab Content */}
                    <div className="p-6 overflow-hidden peer-tabel-dv">
                        <div className="overflow-x-auto">
                            <table className="w-full mq991:w-[1000px]" cellPadding={0} cellSpacing={0}>
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-slate-600">
                                        <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">Employee</th>
                                        <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">Points</th>
                                        <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">Comment</th>
                                        <th className="text-left py-3 px-4 font-semibold text-sm dark:text-white">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTransactions.length > 0 ? (
                                        filteredTransactions.map((transaction: any) => (
                                            <tr key={transaction.id} className="border-b border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600">
                                                <td className="py-3 px-4 dark:text-white">{transaction.employee}</td>
                                                <td className="py-3 px-4 font-semibold dark:text-white">
                                                    <span className={`${activeTab === "given"
                                                        ? "text-red-600 dark:text-red-400"
                                                        : "text-green-600 dark:text-green-400"
                                                        }`}>
                                                        {activeTab === "given" ? "-" : "+"}{transaction.points}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 dark:text-gray-300">{transaction.comment}</td>
                                                <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">{transaction.date}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="py-8 text-center text-gray-500 dark:text-gray-400">
                                                No {activeTab} transactions found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Give Points Modal */}
                {showGivePointsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                            <h2 className="text-2xl font-bold mb-4 dark:text-white">Give Points</h2>

                            <div className="flex flex-col gap-4">
                                <SearchableInputsDropdown
                                    label="Select Employee"
                                    placeHolder="Choose an employee"
                                    options={employeeOptions}
                                    onSelect={(key: string) => {
                                        setSelectedEmployeeId(key);
                                    }}
                                />

                                <InputsNew
                                    label="Points (Max 25)"
                                    contentPlaceholder="Enter points"
                                    type="number"
                                    value={points}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        // if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 25)) {
                                        setPoints(value);
                                        // }
                                    }}
                                // maxValue={25}
                                />

                                <InputsNew
                                    label="Comment"
                                    contentPlaceholder="Add a comment"
                                    type="textarea"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                />

                                <div className="flex gap-3 mt-4">
                                    <Button
                                        title="Cancel"
                                        variant="secondary"
                                        onClick={() => {
                                            setShowGivePointsModal(false);
                                            setSelectedEmployeeId("");
                                            setPoints("");
                                            setComment("");
                                        }}
                                        disabled={createLoading}
                                    />
                                    <Button
                                        title={createLoading ? "Giving..." : "Give Points"}
                                        variant="primary"
                                        onClick={handleGivePoints}
                                        disabled={createLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default PeerPoints;